import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Item, Section } from './types'; // Import shared types
import ItemEditor from './ItemEditor'; // Import the reusable item editor component

const Templates: React.FC = () => {
  const [templates, setTemplates] = useState<any[]>([]);
  const [name, setName] = useState('');
  const [sections, setSections] = useState<Section[]>([]);
  const [itemType, setItemType] = useState<'song' | 'introduction' | 'timeline' | 'general' | 'address' | 'date' | 'note'>('song');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/Templates`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      alert('Failed to load templates. Please try again.');
    }
  };

  const createTemplate = async () => {
    if (!name.trim()) {
      alert('Template name is required.');
      return;
    }
    const jsonContent = JSON.stringify({ sections });
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/Templates`,
        { name, jsonContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTemplates([...templates, response.data]);
      setName('');
      setSections([]);
      alert('Template created successfully!');
    } catch (error) {
      console.error('Failed to create template:', error);
      alert('Failed to create template. Please try again.');
    }
  };

  const addSection = () => {
    setSections([...sections, { 
      label: '', 
      note: '', 
      clientNote: '', 
      items: [], 
      allowClientAdditions: false, 
      allowedItemTypes: [] 
    }]);
  };

  const updateSectionString = (index: number, field: 'label' | 'note' | 'clientNote', value: string) => {
    const updatedSections = [...sections];
    updatedSections[index][field] = value;
    setSections(updatedSections);
  };

  const updateSectionBoolean = (index: number, field: 'allowClientAdditions', value: boolean) => {
    const updatedSections = [...sections];
    updatedSections[index][field] = value;
    setSections(updatedSections);
  };

  const updateSectionAllowedItemTypes = (index: number, itemType: 'song' | 'introduction' | 'timeline' | 'general' | 'address' | 'date' | 'note', checked: boolean) => {
    const updatedSections = [...sections];
    const currentTypes = updatedSections[index].allowedItemTypes;
    if (checked) {
      updatedSections[index].allowedItemTypes = [...currentTypes, itemType];
    } else {
      updatedSections[index].allowedItemTypes = currentTypes.filter(type => type !== itemType);
    }
    setSections(updatedSections);
  };

  const addItem = (sectionIndex: number) => {
    const updatedSections = [...sections];
    switch (itemType) {
      case 'song':
        updatedSections[sectionIndex].items.push({ type: 'song', label: '', song: '', notes: '', clientNotes: '' });
        break;
      case 'introduction':
        updatedSections[sectionIndex].items.push({ type: 'introduction', name: '', song: '', notes: '', clientNotes: '' });
        break;
      case 'timeline':
        updatedSections[sectionIndex].items.push({ type: 'timeline', event: '', time: '', notes: '', clientNotes: '' });
        break;
      case 'general':
        updatedSections[sectionIndex].items.push({ type: 'general', key: '', value: '', notes: '', clientNotes: '' });
        break;
      case 'address':
        updatedSections[sectionIndex].items.push({ type: 'address', label: '', street: '', city: '', state: '', zip: '', notes: '', clientNotes: '' });
        break;
      case 'date':
        updatedSections[sectionIndex].items.push({ type: 'date', label: '', date: '', time: '', notes: '', clientNotes: '' });
        break;
      case 'note':
        updatedSections[sectionIndex].items.push({ type: 'note', content: '', height: 100, notes: '', clientNotes: '' });
        break;
    }
    setSections(updatedSections);
  };

  const updateItem = (sectionIndex: number, itemIndex: number, field: string, value: string) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].items[itemIndex] = {
      ...updatedSections[sectionIndex].items[itemIndex],
      [field]: value,
    };
    setSections(updatedSections);
  };

  const deleteSection = (sectionIndex: number) => {
    setSections(sections.filter((_, i) => i !== sectionIndex));
  };

  const deleteItem = (sectionIndex: number, itemIndex: number) => {
    const updatedSections = [...sections];
    updatedSections[sectionIndex].items = updatedSections[sectionIndex].items.filter(
      (_, i) => i !== itemIndex
    );
    setSections(updatedSections);
  };

  const deleteTemplate = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/Templates/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTemplates(templates.filter(template => template.id !== id));
      alert('Template deleted successfully!');
    } catch (error: any) {
      console.error('Failed to delete template:', error);
      if (error.response && error.response.status === 400) {
        alert(error.response.data);
      } else {
        alert('Failed to delete template. Please try again.');
      }
    }
  };

  return (
    <div className="container mt-5">
      <h2 className="mb-4 text-center">Templates</h2>
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Create New Template</h4>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <label className="form-label">Template Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Enter template name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          {sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="card mb-3 shadow-sm">
              <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Section {sectionIndex + 1}</h5>
                <button
                  onClick={() => deleteSection(sectionIndex)}
                  className="btn btn-danger btn-sm"
                >
                  Delete Section
                </button>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Section Label</label>
                  <input
                    type="text"
                    className="form-control"
                    value={section.label}
                    onChange={(e) => updateSectionString(sectionIndex, 'label', e.target.value)}
                    placeholder="e.g., Ceremony"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Section Note (DJ)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={section.note}
                    onChange={(e) => updateSectionString(sectionIndex, 'note', e.target.value)}
                    placeholder="General note for this section"
                  />
                </div>
                <div className="mb-3">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={section.allowClientAdditions}
                      onChange={(e) => updateSectionBoolean(sectionIndex, 'allowClientAdditions', e.target.checked)}
                    />
                    <label className="form-check-label">
                      Allow clients to add items to this section
                    </label>
                  </div>
                </div>
                {section.allowClientAdditions && (
                  <div className="mb-3">
                    <label className="form-label">Allowed Item Types for Client Additions</label>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={section.allowedItemTypes.includes('song')}
                        onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'song', e.target.checked)}
                      />
                      <label className="form-check-label">Song</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={section.allowedItemTypes.includes('introduction')}
                        onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'introduction', e.target.checked)}
                      />
                      <label className="form-check-label">Introduction</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={section.allowedItemTypes.includes('timeline')}
                        onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'timeline', e.target.checked)}
                      />
                      <label className="form-check-label">Timeline</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={section.allowedItemTypes.includes('general')}
                        onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'general', e.target.checked)}
                      />
                      <label className="form-check-label">General</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={section.allowedItemTypes.includes('address')}
                        onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'address', e.target.checked)}
                      />
                      <label className="form-check-label">Address</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={section.allowedItemTypes.includes('date')}
                        onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'date', e.target.checked)}
                      />
                      <label className="form-check-label">Date</label>
                    </div>
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={section.allowedItemTypes.includes('note')}
                        onChange={(e) => updateSectionAllowedItemTypes(sectionIndex, 'note', e.target.checked)}
                      />
                      <label className="form-check-label">Note</label>
                    </div>
                  </div>
                )}
                <h5>Items</h5>
                {section.items.map((item, itemIndex) => (
                  <div
                    key={itemIndex}
                    className="d-flex flex-column flex-md-row align-items-md-center mb-3 p-3 border rounded bg-light"
                  >
                    <ItemEditor
                      item={item}
                      sectionIndex={sectionIndex}
                      itemIndex={itemIndex}
                      updateItemField={updateItem}
                    />
                    <button
                      onClick={() => deleteItem(sectionIndex, itemIndex)}
                      className="btn btn-danger btn-sm"
                    >
                      Delete Item
                    </button>
                  </div>
                ))}
                <div className="mb-3">
                  <label className="form-label">Item Type</label>
                  <select
                    className="form-select"
                    value={itemType}
                    onChange={(e) => setItemType(e.target.value as 'song' | 'introduction' | 'timeline' | 'general' | 'address' | 'date' | 'note')}
                  >
                    <option value="song">Song</option>
                    <option value="introduction">Introduction</option>
                    <option value="timeline">Timeline</option>
                    <option value="general">General</option>
                    <option value="address">Address</option>
                    <option value="date">Date</option>
                    <option value="note">Note</option>
                  </select>
                </div>
                <button
                  onClick={() => addItem(sectionIndex)}
                  className="btn btn-outline-primary btn-sm"
                >
                  Add Item
                </button>
              </div>
            </div>
          ))}
          <div className="d-flex justify-content-between">
            <button onClick={addSection} className="btn btn-primary">
              Add Section
            </button>
            <button onClick={createTemplate} className="btn btn-success">
              Create Template
            </button>
          </div>
        </div>
      </div>
      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Your Templates ({templates.length})</h4>
        </div>
        <div className="card-body">
          {templates.length === 0 ? (
            <p className="text-muted text-center">No templates found.</p>
          ) : (
            <ul className="list-group list-group-flush">
              {templates.map(template => (
                <li key={template.id} className="list-group-item d-flex justify-content-between align-items-center">
                  {template.name}
                  <div>
                    <button
                      onClick={() => deleteTemplate(template.id)}
                      className="btn btn-danger btn-sm me-2"
                    >
                      Delete
                    </button>
                    <Link to={`/edit-template/${template.id}`} className="btn btn-outline-primary btn-sm">
                      Edit
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Templates;